import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import clsx from 'clsx'

import ControlPanel from '../ControlPanel'
import RunInfoView from './RunInfoView'
import NodeRunTab from './NodeRunTab'
import NodeEditorTab from './NodeEditorTab'
import RunButtons from './RunButtons'
import { GraphNode, NodeData, Tree, GraphResponse, GraphResToTree, RunInfo, RunConfig } from './NodeData'
import { useStoreState, useStoreActions, Elements } from 'react-flow-renderer'
import styles from './ControlView.module.css'
import classNames from 'classnames'
import NodeLiveDataHandler from './NodeLiveDataHandler'
import { useTheme } from '@mui/material/styles'
import { getHeaders } from '../../../utils/common'

function getSelectedNode(nodes: any): GraphNode | null {
  if (nodes !== null && nodes.length === 1 && nodes[0].data && (nodes[0].data.path || '').length > 0) {
    return nodes[0] as GraphNode
  }
  return null
}

function hasKeys(m: any) {
  if (typeof m !== 'object') {
    return false
  }
  return Object.keys(m).length > 0
}

function newRunInfo(): RunInfo {
  return {
    config: {
      enabled: false,
      mock_inputs: {},
      mock_processors: {},
    },
  }
}

type urlParams = {
  sessionID: string
  fileName: string
}

enum Tab {
  Edit,
  Run,
}

/**
 * Component for managing control and run views of the application.
 *
 * @param {Object} props - The props for the `ControlView` component.
 * @param {Tree} props.tree - The tree data representing the graph.
 * @param {Function} props.setTree - A function to update the tree data.
 * @returns {JSX.Element} The JSX element representing the `ControlView` component.
 */
export default function ControlView({ tree, setTree, deploySelected }: { tree: Tree; setTree: (tree: Tree) => void; deploySelected: any }) {
  const { sessionID, fileName } = useParams<urlParams>()
  const theme = useTheme()

  const runTreeURL = `/mqttapi/v1/session/${sessionID}/file/${fileName}/tree/run`
  const undoURL = `/mqttapi/v1/session/${sessionID}/file/${fileName}/tree/undo`
  const redoURL = `/mqttapi/v1/session/${sessionID}/file/${fileName}/tree/redo`

  // RUN VIEW STUFF
  const [isRunning, setIsRunning] = useState(false)
  const [isRunningNode, setIsRunningNode] = useState(false)
  const [runInfo, setRunInfo] = useState<RunInfo>(newRunInfo())
  const [isRunningOn, setIsRunningOn] = useState(runInfo.config.enabled)
  const [runError, setRunError] = useState<Error | null>(null)
  const [showRunInfo, setShowRunInfo] = useState(false)

  const allNodes = useStoreState<Elements<NodeData>>((state) => state.nodes)
  const allEdges = useStoreState<Elements<NodeData>>((state) => state.edges)
  const selectedNodes = useStoreState<Elements<NodeData> | null>((store) => store.selectedElements)
  const setSelectedNodes = useStoreActions((actions) => actions.setSelectedElements)

  // Used by editor window for expanding the view
  const [widerView, setWiderView] = useState(localStorage.getItem('node-view-expanded') === 'true')
  const [activeTab, setActiveTab] = useState(Tab.Edit)

  const myHeaders = getHeaders()
  myHeaders.append('Content-Type', 'application/json')

  useEffect(() => {
    localStorage.setItem('node-view-expanded', widerView ? 'true' : 'false')
  }, [widerView])

  const toggleWiderView = useCallback(() => {
    setWiderView(!widerView)
  }, [setWiderView, widerView])

  // Clear selection when the file changes.
  useEffect(() => {
    setSelectedNodes([])
  }, [fileName, allNodes, setSelectedNodes])

  const submitUndo = useCallback(() => {
    fetch(undoURL, {
      method: 'POST',
      headers: myHeaders,
    })
      .then(async (response) => {
        if (response.ok) {
          return response.json()
        }
        const text = await response.text()
        throw new Error(`${response.statusText}: ${text}`)
      })
      .then((res: GraphResponse) => {
        setTree(GraphResToTree(res))
        setSelectedNodes([])
      })
      .catch((error) => console.log(error))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undoURL, setSelectedNodes, setTree])

  const submitRedo = useCallback(() => {
    fetch(redoURL, {
      method: 'POST',
      headers: myHeaders,
    })
      .then(async (response) => {
        if (response.ok) {
          return response.json()
        }
        const text = await response.text()
        throw new Error(`${response.statusText}: ${text}`)
      })
      .then((res: GraphResponse) => {
        setTree(GraphResToTree(res))
        setSelectedNodes([])
      })
      .catch((error) => console.log(error))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redoURL, setSelectedNodes, setTree])

  // Execute on page open.
  useEffect(() => {
    let subscribed = true
    setIsRunning(true)
    setRunInfo(newRunInfo())
    setRunError(null)

    fetch(runTreeURL, { headers: myHeaders, method: 'GET' })
      .then(async (response) => {
        if (response.ok) {
          setRunError(null)
          return response.json()
        }
        const text = await response.text()
        throw new Error(`${response.statusText}: ${text}`)
      })
      .then((json) => (subscribed ? setRunInfo(json) : null))
      .catch((error) => (subscribed ? setRunError(error) : null))
      .finally(() => (subscribed ? setIsRunning(false) : null))

    return () => {
      subscribed = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, runTreeURL, sessionID, fileName])

  const setRunConfig = useCallback(
    (conf: RunConfig, onFinished: () => void) => {
      let subscribed = true

      if (tree.read_only) {
        return
      }

      setRunError(null)
      if (conf.enabled) {
        setIsRunning(true)
      } else {
        setIsRunning(false)
        setTimeout(() => {
          const tmpInfo = JSON.parse(JSON.stringify(runInfo))
          tmpInfo.config = conf
          tmpInfo.result = undefined
          setRunInfo(tmpInfo)
        }, 1)
      }

      fetch(runTreeURL, {
        method: 'POST',
        headers: myHeaders,
        body: JSON.stringify(conf),
      })
        .then(async (response) => {
          if (response.ok) {
            setRunError(null)
            return response.json()
          }
          const text = await response.text()
          throw new Error(`${response.statusText}: ${text}`)
        })
        .then((json) => {
          // setShowRunInfo(true);
          if (!subscribed) {
            return
          }
          const tmpInfo = JSON.parse(JSON.stringify(runInfo))
          tmpInfo.config = conf
          tmpInfo.result = json.result
          setRunInfo(tmpInfo)
        })
        .catch((error) => (subscribed ? setRunError(error) : null))
        .finally(() => {
          if (!subscribed) {
            return
          }
          setIsRunning(false)
          onFinished()
        })

      return () => (subscribed = false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runTreeURL, runInfo, tree, showRunInfo]
  )

  const node = getSelectedNode(selectedNodes)
  useEffect(() => {
    // * when active tab is edit or no node is selected or after selection of node user clicked on blank area
    // * then stop the live stream
    if (activeTab === 0 || node === null) {
      setIsRunningNode(false)
      setActiveTab(0)
    }
  }, [activeTab, node, selectedNodes])
  return (
    <>
      {/* <div className={styles.controlPanel} style={{ background: theme.palette.primary.light }}>
        <ControlPanel canUndo={tree.has_undo} canRedo={tree.has_redo} onUndo={submitUndo} onRedo={submitRedo}>
          <RunButtons
            hasErrored={runError !== null || (runInfo.result?.error || null) !== null}
            needsAction={hasKeys(runInfo?.result?.missing_inputs) || hasKeys(runInfo?.result?.missing_processors)}
            isEnabled={runInfo.config.enabled}
            isRunning={isRunning}
            // todo: 11
            setIsEnabled={(s: boolean) => {
              if (tree.read_only) {
                return
              }
              runInfo.config.enabled = s
              setRunConfig(runInfo.config, () => {})
              // onRunCallLive(!s);
              setIsRunningOn(!isRunningOn)
            }}
            toggleModal={() => {
              setShowRunInfo(!showRunInfo)
            }}
          />
        </ControlPanel>
      </div> */}
      <div
        style={{
          display: showRunInfo && node === null ? undefined : 'none',
        }}
        className={styles.runViewWindow}
      >
        <div className={clsx(styles.nodeViewBox, widerView ? styles.nodeViewBoxWide : styles.nodeViewBoxNarrow)}>
          <button onClick={toggleWiderView} className={styles.expandHandle}>
            {widerView ? '>' : '<'}
          </button>
          <RunInfoView
            tree={tree}
            isRunning={isRunning}
            runError={runError}
            runInfo={runInfo}
            allNodes={allNodes}
            close={() => setShowRunInfo(false)}
          />
        </div>
      </div>
      {/* {isRunningOn && ( */}
      <div
        style={{
          display: isRunningOn && node === null ? undefined : 'none',
        }}
        className={styles.runViewWindow}
      >
        <div className={clsx(styles.nodeViewBox, widerView ? styles.nodeViewBoxWide : styles.nodeViewBoxNarrow)}>
          <button onClick={toggleWiderView} className={styles.expandHandle}>
            {widerView ? '>' : '<'}
          </button>
          <NodeLiveDataHandler
            isRunning={isRunningOn}
            runInfo={runInfo}
            sessionID={sessionID || ''}
            fileName={fileName || ''}
            deploySelected={deploySelected}
          />
        </div>
      </div>
      {/* )} */}
      <div style={{ display: node === null ? 'none' : undefined }} className={styles.nodeViewWindow}>
        <div className={clsx(styles.nodeViewBox, widerView ? styles.nodeViewBoxWide : styles.nodeViewBoxNarrow)}>
          <button onClick={toggleWiderView} className={styles.expandHandle}>
            {widerView ? '>' : '<'}
          </button>
          <div className={styles.tabButtons}>
            <button className={classNames(activeTab === Tab.Edit ? styles.tabButtonActive : null)} onClick={() => setActiveTab(Tab.Edit)}>
              Edit
            </button>
            
            {/* <button
              className={classNames(activeTab === Tab.Run ? styles.tabButtonActive : null)}
              onClick={() => {
                setActiveTab(Tab.Run)
                setIsRunningNode(true)
              }}
            >
              Run Results
            </button> */}
          </div>
          <div className={classNames(styles.tabContent)}>
            <div
              style={{
                display: activeTab === Tab.Edit ? undefined : 'none',
              }}
              className={styles.nodeViewContent}
            >
              <NodeEditorTab
                readOnly={tree.read_only}
                setTree={setTree}
                canUndo={tree.has_undo}
                canRedo={tree.has_redo}
                submitUndo={submitUndo}
                submitRedo={submitRedo}
                allNodes={allNodes}
                allEdges={allEdges}
                selectedNodes={selectedNodes}
                setSelectedNodes={setSelectedNodes}
              />
            </div>
            <div
              style={{
                display: activeTab === Tab.Run ? undefined : 'none',
              }}
              className={styles.nodeViewContent}
            >
              {node && node?.data?.kind === 'processor' ? (
                <NodeLiveDataHandler
                  isRunning={isRunningNode}
                  runInfo={runInfo}
                  sessionID={sessionID || ''}
                  fileName={fileName || ''}
                  nodePath={node?.data?.path}
                  deploySelected={deploySelected}
                />
              ) : (
                <NodeRunTab
                  tree={tree}
                  isRunning={isRunning}
                  runError={runError}
                  runInfo={runInfo}
                  setRunConfig={setRunConfig}
                  allNodes={allNodes}
                  selectedNodes={selectedNodes}
                  setSelectedNodes={setSelectedNodes}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
